import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useTheme,
  type Theme,
  type Density,
  getAccent,
  setAccent,
  applyAccent,
  getDensity,
  setDensity,
  applyDensity,
  getReducedMotion,
  setReducedMotion,
  applyReducedMotion,
  ACCENT_OPTIONS,
} from "@/lib/theme";
import { SettingsShell } from "@/components/SettingsShell";

/**
 * /settings/appearance — mirrors the APPEARANCE panel in
 * designs/Settings.html. Single panel with four .opt rows:
 * theme, accent color, density, reduce motion.
 */

function Appearance() {
  const { preference, set } = useTheme();
  const [accent, setAccentState] = useState<string>(() => getAccent());
  const [density, setDensityState] = useState<Density>(() => getDensity());
  const [reducedMotion, setReducedMotionState] = useState<boolean>(() =>
    getReducedMotion(),
  );

  const themeOptions: Array<{ value: Theme; label: string }> = [
    { value: "light", label: "Light" },
    { value: "dark", label: "Dark" },
    { value: "system", label: "System" },
  ];

  return (
    <SettingsShell>
      <div className="panel">
        <div className="panel-head">
          <h3>Appearance</h3>
          <p>Personalise how Because looks on this device.</p>
        </div>
        <div className="panel-body">
          {/* Theme */}
          <div className="opt" style={{ paddingTop: 0 }}>
            <div className="ot">
              <div className="otit">Theme</div>
              <div className="odesc">Switch between light and dark.</div>
            </div>
            <div className="seg" role="radiogroup" aria-label="Theme">
              {themeOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={preference === opt.value}
                  className={preference === opt.value ? "on" : ""}
                  onClick={() => set(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Accent color */}
          <div className="opt">
            <div className="ot">
              <div className="otit">Accent color</div>
              <div className="odesc">
                Used for primary actions and highlights.
              </div>
            </div>
            <div
              className="swatch-pick"
              role="radiogroup"
              aria-label="Accent color"
            >
              {ACCENT_OPTIONS.map((a) => (
                <button
                  key={a.value}
                  type="button"
                  role="radio"
                  aria-checked={accent === a.value}
                  aria-label={a.label}
                  className={`sw${accent === a.value ? " on" : ""}`}
                  style={{ background: a.base }}
                  onClick={() => {
                    setAccentState(a.value);
                    setAccent(a.value);
                    applyAccent(a.value);
                  }}
                />
              ))}
            </div>
          </div>

          {/* Density */}
          <div className="opt">
            <div className="ot">
              <div className="otit">Density</div>
              <div className="odesc">Compact fits more on screen.</div>
            </div>
            <div className="seg" role="radiogroup" aria-label="Density">
              <button
                type="button"
                role="radio"
                aria-checked={density === "comfortable"}
                className={density === "comfortable" ? "on" : ""}
                onClick={() => {
                  setDensityState("comfortable");
                  setDensity("comfortable");
                  applyDensity("comfortable");
                }}
              >
                Comfortable
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={density === "compact"}
                className={density === "compact" ? "on" : ""}
                onClick={() => {
                  setDensityState("compact");
                  setDensity("compact");
                  applyDensity("compact");
                }}
              >
                Compact
              </button>
            </div>
          </div>

          {/* Reduce motion */}
          <div className="opt">
            <div className="ot">
              <div className="otit">Reduce motion</div>
              <div className="odesc">Minimise animations and transitions.</div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={reducedMotion}
              aria-label="Reduce motion"
              className={`switch${reducedMotion ? " on" : ""}`}
              onClick={() => {
                const val = !reducedMotion;
                setReducedMotionState(val);
                setReducedMotion(val);
                applyReducedMotion(val);
              }}
            />
          </div>
        </div>
      </div>
    </SettingsShell>
  );
}

export const Route = createFileRoute("/settings/appearance")({
  component: Appearance,
});
